import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  TrendingUp,
  History,
  ArrowDown,
  ArrowUp,
  ShoppingCart,
  Building,
  Clock,
} from 'lucide-react'
import { useAdjustInventory, useInventoryLedger } from '@/hooks/inventory'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { getApiErrorMessage } from '@/api/client'
import { useToast } from '@/components/ToastProvider'

function fmtDate(value?: string) {
  return value ? new Date(value).toLocaleString('zh-CN') : '-'
}

function getCategory(partName: string) {
  const name = partName.toLowerCase()
  if (name.includes('mri') || name.includes('核磁') || name.includes('线圈')) return 'MRI 配件'
  if (name.includes('ct') || name.includes('探测器') || name.includes('球管')) return 'CT 配件'
  if (name.includes('x光') || name.includes('xray') || name.includes('射线')) return 'X 光配件'
  if (name.includes('dsa') || name.includes('血管')) return 'DSA 配件'
  if (name.includes('超声') || name.includes('probe')) return '超声配件'
  return '通用配件'
}

function getBrand(partName: string, specification?: string) {
  const value = `${partName} ${specification || ''}`.toLowerCase()
  if (value.includes('西门子') || value.includes('siemens')) return '西门子'
  if (value.includes('philips') || value.includes('飞利浦')) return '飞利浦'
  if (value.includes('ge')) return 'GE'
  if (value.includes('联影') || value.includes('united')) return '联影'
  return '通用'
}

function AdjustStockModal({
  mode,
  skuCode,
  onClose,
}: {
  mode: 'in' | 'out'
  skuCode: string
  onClose: () => void
}) {
  const toast = useToast()
  const adjustMutation = useAdjustInventory()
  const [quantity, setQuantity] = useState(1)
  const [remark, setRemark] = useState(mode === 'in' ? '手动入库' : '手动出库')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      await adjustMutation.mutateAsync({
        skuCode,
        data: {
          deltaOnHand: mode === 'in' ? quantity : -quantity,
          remark,
        },
      })
      toast.success(mode === 'in' ? '入库完成' : '出库完成')
      onClose()
    } catch (error) {
      toast.error(getApiErrorMessage(error))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          {mode === 'in' ? '手动入库' : '手动出库'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">数量</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(event) => setQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">备注</label>
            <textarea
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              rows={3}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-600 transition hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={adjustMutation.isPending}
              className="rounded-xl bg-primary-500 px-4 py-2 text-sm text-white transition hover:bg-primary-600 disabled:opacity-50"
            >
              {adjustMutation.isPending ? '提交中...' : '确认'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function PartsDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const skuCode = id || ''
  const toast = useToast()
  const [adjustMode, setAdjustMode] = useState<'in' | 'out' | null>(null)

  const { data: part, isLoading, error } = useInventoryLedger(skuCode)

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载配件详情..." />
      </div>
    )
  }

  if (error || !part) {
    return (
      <div className="p-6">
        <InlineError message={getApiErrorMessage(error) || '未找到该配件信息'} />
      </div>
    )
  }

  const category = getCategory(part.partName)
  const brand = getBrand(part.partName, part.specification)
  const currentStock = part.availableQty
  const safetyStock = part.safetyStockQty
  const maxStock = Math.max(safetyStock * 2, 10)
  const suggestedRestock = Math.max(0, safetyStock - currentStock)

  const stockStatus =
    currentStock === 0
      ? { label: '缺货', bg: 'from-red-500 to-red-600', desc: '需要尽快补货' }
      : currentStock < safetyStock
        ? { label: '库存偏低', bg: 'from-amber-500 to-amber-600', desc: '建议补货' }
        : { label: '库存正常', bg: 'from-green-500 to-green-600', desc: '库存充足' }

  const latestSupplier = part.supplierInsight?.supplierName || part.purchaseHistory?.[0]?.supplierName || '-'
  const latestActivities = [...(part.ledgers || [])].slice(-5).reverse()

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/supply-chain/parts/list" className="rounded-lg p-2 transition hover:bg-gray-100">
            <TrendingUp className="h-5 w-5 rotate-180 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">配件详情</h1>
            <p className="mt-1 text-sm text-gray-500">
              {part.skuCode} | {part.partName}
            </p>
          </div>
        </div>
        <button
          onClick={() =>
            navigate('/supply-chain/purchase/create', {
              state: {
                source: 'inventory-replenishment',
                preselectedItems: [
                  { skuCode: part.skuCode, partName: part.partName, quantity: suggestedRestock || 1 },
                ],
              },
            })
          }
          className="rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
        >
          发起补货申请
        </button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">基本信息</h2>
            </div>
            <div className="p-5">
              <div className="flex items-start gap-6">
                <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-blue-50 text-4xl">
                  备
                </div>
                <div className="grid flex-1 grid-cols-3 gap-4">
                  <div>
                    <div className="mb-1 text-xs text-gray-400">配件名称</div>
                    <div className="font-medium text-gray-900">{part.partName}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">配件编码</div>
                    <div className="font-medium text-gray-900">{part.skuCode}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">规格型号</div>
                    <div className="font-medium text-gray-900">{part.specification || '-'}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">配件分类</div>
                    <div className="font-medium text-gray-900">{category}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">品牌</div>
                    <div className="font-medium text-gray-900">{brand}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">单位</div>
                    <div className="font-medium text-gray-900">{part.unit}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">仓库</div>
                    <div className="font-medium text-gray-900">{part.warehouseCode}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">总库存</div>
                    <div className="font-medium text-gray-900">{part.onHandQty}</div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-gray-400">可用库存</div>
                    <div className="font-medium text-gray-900">{part.availableQty}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">库存信息</h2>
              <Link
                to={`/supply-chain/inventory/ledger/${part.skuCode}`}
                className="text-sm font-medium text-primary-600 hover:underline"
              >
                查看台账
              </Link>
            </div>
            <div className="p-5">
              <div className="mb-6 grid grid-cols-4 gap-4">
                <div className="rounded-xl bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{currentStock}</div>
                  <div className="mt-1 text-sm text-gray-500">当前库存</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{safetyStock}</div>
                  <div className="mt-1 text-sm text-gray-500">安全库存</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{maxStock}</div>
                  <div className="mt-1 text-sm text-gray-500">建议上限</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-4 text-center">
                  <div className="text-2xl font-bold text-primary-600">{suggestedRestock}</div>
                  <div className="mt-1 text-sm text-gray-500">建议补货</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() =>
                    navigate('/supply-chain/purchase/create', {
                      state: {
                        source: 'inventory-replenishment',
                        preselectedItems: [
                          { skuCode: part.skuCode, partName: part.partName, quantity: suggestedRestock || 1 },
                        ],
                      },
                    })
                  }
                  className="flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
                >
                  <ShoppingCart className="h-4 w-4" />
                  发起补货申请
                </button>
                <button
                  onClick={() => setAdjustMode('in')}
                  className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600 transition hover:bg-blue-100"
                >
                  <ArrowDown className="h-4 w-4" />
                  手动入库
                </button>
                <button
                  onClick={() => setAdjustMode('out')}
                  className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-2 text-sm font-medium text-amber-600 transition hover:bg-amber-100"
                >
                  <ArrowUp className="h-4 w-4" />
                  手动出库
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="text-lg font-bold text-gray-900">关联工单</h2>
            </div>
            <div className="p-5">
              {part.relatedWorkOrders?.length ? (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500">
                      <th className="pb-3 font-medium">工单号</th>
                      <th className="pb-3 font-medium">医院</th>
                      <th className="pb-3 font-medium">需求 / 已预留 / 已消耗</th>
                      <th className="pb-3 font-medium">状态</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {part.relatedWorkOrders.map((workOrder) => (
                      <tr key={workOrder.workOrderId} className="border-t border-gray-100">
                        <td className="py-3 font-medium text-primary-600">
                          <Link to={`/work-orders/${workOrder.workOrderId}`}>{workOrder.workOrderNo}</Link>
                        </td>
                        <td className="py-3 text-gray-600">{workOrder.hospitalName || '-'}</td>
                        <td className="py-3 text-gray-600">
                          {workOrder.requiredQty} / {workOrder.reservedQty} / {workOrder.consumedQty}
                        </td>
                        <td className="py-3 text-gray-600">{workOrder.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyData description="暂无关联工单" />
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className={`rounded-2xl bg-gradient-to-br ${stockStatus.bg} p-5 text-white`}>
            <div className="mb-4">
              <div className="text-lg font-bold">{stockStatus.label}</div>
              <div className="text-xs opacity-80">{stockStatus.desc}</div>
            </div>
            <div className="rounded-xl bg-white/10 p-3">
              <div className="mb-2 flex justify-between text-sm">
                <span>当前库存</span>
                <span className="font-bold">
                  {currentStock} {part.unit}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>安全库存</span>
                <span>
                  {safetyStock} {part.unit}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="font-bold text-gray-900">供应商信息</h2>
            </div>
            <div className="space-y-4 p-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                  <Building className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{latestSupplier}</div>
                  <div className="text-xs text-gray-500">
                    采购次数: {part.supplierInsight?.purchaseCount ?? part.purchaseHistory?.length ?? 0}
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  最近采购: {fmtDate(part.supplierInsight?.lastPurchaseAt || part.purchaseHistory?.[0]?.createdAt)}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 p-5">
              <h2 className="font-bold text-gray-900">最近动态</h2>
            </div>
            <div className="p-5">
              {latestActivities.length ? (
                <div className="space-y-4">
                  {latestActivities.map((activity) => (
                    <div key={activity.id} className="flex gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <div>
                        <div className="text-sm text-gray-700">
                          {activity.movementType} ({activity.deltaOnHand > 0 ? '+' : ''}
                          {activity.deltaOnHand})
                        </div>
                        <div className="text-xs text-gray-400">
                          {activity.remark || '-'} | {fmtDate(activity.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyData description="暂无动态记录" />
              )}
            </div>
          </div>
        </div>
      </div>

      {adjustMode ? (
        <AdjustStockModal mode={adjustMode} skuCode={part.skuCode} onClose={() => setAdjustMode(null)} />
      ) : null}
    </div>
  )
}
